import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { CTAContainer } from '@/components/layout/CTAContainer';
import { Tabs } from '@/components/layout/Tabs';
import { ErrorSnackbar } from '@/components/ui/ErrorSnackbar';
import { PageLoader } from '@/components/ui/PageLoader';
import { Typography } from '@/components/ui/Typography';
import { MQTTContext } from '@/context/MQTT';

import {
  getDeliveryList,
  initDeliveryList,
  ReceivingProcessConst,
  setDeliveryProcess,
  unlockDeliveries,
} from '@/features/deliveries/deliveriesSlice';
import {
  deleteTags,
  initDevices,
  removeDeviceInUse,
} from '@/features/devices/devicesSlice';
import { useSubscribeDeviceStatus } from '@/hooks/mqtt/useSubscribeDeviceStatus';
import { useReceivingDeliveries } from '@/hooks/useReceivingDeliveries';
import { useReceivingPage } from '@/hooks/useReceivingPage';
import { useSelector } from '@/hooks/useSelector';
import DeliveryList from '@/pages/Receiving/PageReceiving/DeliveryList';
import ModalBoxes from '@/pages/Receiving/PageReceiving/ModalBoxes';
import ModalNewDelivery from '@/pages/Receiving/PageReceiving/ModalNewDelivery';
import {
  ReceivingActiveFilters,
  ReceivingConfirmedFilters,
} from '@/pages/Receiving/PageReceiving/ReceivingFilters';
import {
  StyledNoContentWrapper,
  StyledTab,
  StyledTitle,
  StyledWrapper,
} from '@/pages/Receiving/PageReceiving/style';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';
import { ScanDevice } from '@/types/device';
import { convertDateToDateTime } from '@/utils/date';
import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

const PageReceiving: FC = memo((): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false);
  const location = useLocation();
  const [openInfoDeliveryModal, setOpenInfoDeliveryModal] =
    useState<boolean>(false);
  const [openBoxesModal, setOpenBoxesModal] = useState<boolean>(false);

  const [selectedBoxes, setSelectedBoxes] = useState<string[]>([]);
  const [selectedDeliveryCode, setSelectedDeliveryCode] = useState('');
  const [selectedTab, setSelectedTab] = useState<number>(
    location.state?.tab ?? 0
  );
  const [searchDeliveries, setSearchDeliveries] = useState<boolean>(true);
  const STATUS_TABS = [t('receiving.tab.active'), t('receiving.tab.confirmed')];
  const subscribeDevice = useSubscribeDeviceStatus();
  const { setDeviceIdOnMqttClient } = React.useContext(MQTTContext);

  const searchSnapshotRef = useRef<{
    startDate: Date | null;
    endDate: Date | null;
    deliveryNumber: string;
    searchType: 'DATES' | 'DELIVERY' | null;
  }>({
    startDate: null,
    endDate: null,
    deliveryNumber: '',
    searchType: null,
  });

  useEffect(() => {
    const resetDevices = async () => {

      const storage = sessionStorage.getItem('deviceInUse');

      const deviceInStorage = storage
        ? (JSON.parse(storage) as Required<ScanDevice>)
        : undefined;
      if (deviceInStorage) {
        sessionStorage.removeItem('deviceInUse');

        await dispatch(deleteTags());
        subscribeDevice.publish(deviceInStorage.deviceId, undefined);
        setDeviceIdOnMqttClient(undefined);
        dispatch(removeDeviceInUse());
      }
      dispatch(initDevices());
    }

    resetDevices();
  }, [dispatch])

  useEffect(() => {
    if (location.state?.tab !== undefined) {
      history.replace(location.pathname, {});
    }
  }, [history, location.pathname, location.state?.tab]);

  useEffect(() => {
    if (selectedTab === 1) {
      setSearchDeliveries(true);
    }
  }, [selectedTab]);

  const { CONFIRMED } = ReceivingStatus;

  const receivingPage = useReceivingPage();
  const {
    state: { filters, startDate, endDate, deliveryNumber },
    actions: { resetFilters, getInitialDates },
  } = receivingPage;

  const { activeDeliveries, confirmedDeliveries } =
    useReceivingDeliveries(filters);

  const { selectedDeliveries, error, isLoading } = useSelector(
    state => state.deliveries
  );

  const { error: errorDeliveryInUse } = useSelector(
    state => state.deliveryProducts
  );

  const unlockReceiving = useCallback(async () => {
    const isConfirmedTab = selectedTab === 1;

    if (!isConfirmedTab) {
      await dispatch(unlockDeliveries());
      dispatch(initDeliveryList());
      dispatch(getDeliveryList());
    } else {
      if (searchDeliveries && (startDate || endDate || deliveryNumber)) {
        const searchType: 'DATES' | 'DELIVERY' =
          deliveryNumber.trim() !== '' ? 'DELIVERY' : 'DATES';

        await dispatch(unlockDeliveries());
        dispatch(initDeliveryList());
        dispatch(
          getDeliveryList({
            status: CONFIRMED,
            fromDate: startDate
              ? convertDateToDateTime(startDate).toFormat('yyyyMMdd')
              : undefined,
            endDate: endDate
              ? convertDateToDateTime(endDate).toFormat('yyyyMMdd')
              : undefined,
            deliveryNumber: deliveryNumber || undefined,
          })
        );

        searchSnapshotRef.current = {
          startDate,
          endDate,
          deliveryNumber,
          searchType,
        };

        setSearchDeliveries(false);
      }
    }
  }, [
    dispatch,
    searchDeliveries,
    selectedTab,
    startDate,
    endDate,
    deliveryNumber,
    CONFIRMED,
  ]);

  useEffect(() => {
    unlockReceiving();
  }, [unlockReceiving, selectedTab]);

  useEffect(() => {
    if (error || errorDeliveryInUse) {
      setIsErrorVisible(true);
    }
  }, [error, errorDeliveryInUse]);

  const { start: initialStart, end: initialEnd } = getInitialDates();

  const hasActiveFilters =
    (!!deliveryNumber.trim() && startDate === null && endDate === null) ||
    ((startDate?.getTime() !== initialStart.getTime() ||
      endDate?.getTime() !== initialEnd.getTime()) &&
      startDate !== null &&
      endDate !== null &&
      !deliveryNumber.trim());

  const handleOpenBoxesModal = (
    deliveryCode: string,
    boxes: string[]
  ): void => {
    setOpenBoxesModal(true);
    setSelectedBoxes(boxes);
    setSelectedDeliveryCode(deliveryCode);
  };

  const handleSelectDeliveries = (): void => {
    if (
      selectedDeliveries.length === 1 &&
      selectedDeliveries[0].status === ReceivingStatus.PARTIAL
    ) {
      dispatch(setDeliveryProcess(ReceivingProcessConst.RCP));
      history.push(AppRoutes.RECEIVING_DELIVERIES);
    } else {
      setOpenInfoDeliveryModal(true);
    }
  };

  return (
    <>
      <StyledTitle>
        <Typography size="lg" font="heavy" as="h1">
          {t('list.title.deliveries_available')}
        </Typography>
      </StyledTitle>
      <Tabs
        values={STATUS_TABS}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
      <StyledTab>
        {selectedTab === 0 && (
          <StyledWrapper>
            <ReceivingActiveFilters receivingPage={receivingPage} />
            {isLoading && activeDeliveries.length === 0 ? (
              <PageLoader />
            ) : activeDeliveries.length > 0 ? (
              <DeliveryList
                deliveries={activeDeliveries}
                onBoxesClick={handleOpenBoxesModal}
              />
            ) : (
              <StyledNoContentWrapper>
                <Typography size="lg" font="heavy" as="h1">
                  {t('list.title.no_active_deliveries')}
                </Typography>
              </StyledNoContentWrapper>
            )}
          </StyledWrapper>
        )}
        {selectedTab === 1 && (
          <>
            <StyledWrapper>
              <ReceivingConfirmedFilters
                receivingPage={receivingPage}
                setSearchDeliveries={setSearchDeliveries}
                last={searchSnapshotRef.current}
              />
              {isLoading && activeDeliveries.length === 0 ? (
                <PageLoader />
              ) : confirmedDeliveries.length > 0 ? (
                <DeliveryList
                  deliveries={confirmedDeliveries}
                  onBoxesClick={handleOpenBoxesModal}
                />
              ) : (
                <StyledNoContentWrapper>
                  <Typography size="lg" font="heavy" as="h1">
                    {t('list.title.no_confirmed_deliveries')}
                  </Typography>
                </StyledNoContentWrapper>
              )}
            </StyledWrapper>
          </>
        )}
      </StyledTab>

      <ErrorSnackbar
        open={isErrorVisible}
        setIsOpen={setIsErrorVisible}
        errorMessage={errorDeliveryInUse?.body || t('error.receiving')}
      />

      {selectedTab === 0 ? (
        <CTAContainer
          type="SELECT"
          onClick={handleSelectDeliveries}
          disabled={selectedDeliveries.length === 0}
        />
      ) : (
        <CTAContainer
          type="TWO_BUTTONS"
          onClick={(): void => setSearchDeliveries(true)}
          onBackClick={() => {
            resetFilters();
            setSearchDeliveries(true);
          }}
          disabledMainAction={!hasActiveFilters}
          disabled={!hasActiveFilters}
          backButtonLabel={t('reset')}
          mainButtonLabel={t('search')}
          mainButtonCentered={true}
        />
      )}

      <ModalBoxes
        open={openBoxesModal}
        onClose={(): void => setOpenBoxesModal(false)}
        deliveryCode={selectedDeliveryCode}
        boxes={selectedBoxes}
      />

      <ModalNewDelivery
        selectedDeliveries={selectedDeliveries}
        open={openInfoDeliveryModal}
        setOpen={setOpenInfoDeliveryModal}
      />
    </>
  );
});

PageReceiving.displayName = 'PageReceiving';

export default PageReceiving;
